import React, { FC, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  AdditionalPanel,
  BottomLayout,
  LayoutContainer,
  LeftContainer,
  NirvanaButton,
  PanelBlock,
  TopPanelBlock,
} from "./styled";
import { Panel } from "./Panel";
import { Main } from "./MainContainer";
import { PanelWidthDrag } from "./PanelWidthDrag";
import { getThemeName } from "../../ducks/theme/selectors";
import { getLocationType } from "../../ducks/location/selectors";
import { useModules } from "../../hooks/appConfig/useModules";
import { useNirvanaMode } from "../../hooks/layout/useNirvanaMode";
import { SharedLayoutProps } from "./types";

export const SharedLayout: FC<SharedLayoutProps> = ({
  main,
  panel,
  portal,
  bottom,
  additionalControls,
  mainControls,
  topControls,
  bottomState,
  bottomHeight,
}) => {
  const { t } = useTranslation("common");
  const themeName = useSelector(getThemeName);
  const locationType = useSelector(getLocationType);
  const [isResizing, setIsResizing] = useState(false);
  const modules = useModules();
  const { isNirvanaMode, toggleNirvanaMode } = useNirvanaMode();

  const panelSettings = useMemo(
    () => modules.panel?.find(({ route }) => route === locationType),
    [locationType, modules.panel],
  );

  const isResizable = useMemo(() => Boolean(panelSettings?.layoutOptions?.resizable), [panelSettings?.layoutOptions]);

  return (
    <LayoutContainer>
      {bottomState !== "fullscreen" && (
        <LeftContainer $isHidden={isNirvanaMode}>
          <Panel>
            {isResizable && <PanelWidthDrag setIsResizing={setIsResizing} />}
            {!isResizing && panel}
          </Panel>
          <NirvanaButton
            title={t("nirvanaMode")}
            $isNirvanaMode={isNirvanaMode}
            $bottomHeight={bottomState === "opened" ? bottomHeight : 0}
            onClick={toggleNirvanaMode}
          />
        </LeftContainer>
      )}
      <Main bottomSlot={bottom} bottomState={bottomState} bottomHeight={bottomHeight}>
        {main}
        <TopPanelBlock>{topControls}</TopPanelBlock>
        <BottomLayout bottomOpened={bottomState === "opened"} bottomHeight={bottomHeight} themeName={themeName}>
          <PanelBlock>{mainControls}</PanelBlock>
          <AdditionalPanel>{additionalControls}</AdditionalPanel>
        </BottomLayout>
      </Main>
      <div>{portal}</div>
    </LayoutContainer>
  );
};
